import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { emailAvailabilityStatus } from 'services/clients/user';
import { Routes } from 'constants/pages';
import { useSegment } from 'common/hooks/segment';
import { SignUpTemplate } from 'layouts/SignUpTemplate';
import { CircularProgress } from '@mui/material';
import BlueCargoLogo from 'components/BlueCargoLogo';
import { CreateEmail } from './pages/create-email/CreateEmail';
import { CreateProfile } from './pages/create-profile/CreateProfile';

enum Stage {
  Email = 0,
  Profile = 1,
}

export const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const email = params.get('email');
  const { track } = useSegment();

  const [stage, setStage] = React.useState<Stage>(Stage.Email);

  // If an email is provided on mount, check the current status
  // of the email to determine the correct stage to show
  const getStatus = useMutation(emailAvailabilityStatus);

  React.useEffect(() => {
    if (email) {
      getStatus.mutate(email, {
        // eslint-disable-next-line no-console
        onError: (e) => console.log(e),
        onSuccess: (code) => {
          switch (code) {
            case 'needs_account':
              // go to profile creation
              track('User submitted email', { email, validated: true });
              setStage(Stage.Profile);
              break;
            case 'needs_validation':
              // go to email confirmation
              track('User submitted email', { email, validated: false });
              navigate(`${Routes.EmailConfirmation}?email=${encodeURIComponent(email || '')}`);
              break;
            default:
              // nothing to be done
              break;
          }
        },
      });
    } else {
      setStage(Stage.Email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  // Callback for the various stages of signup to continue the process
  const proceed = React.useCallback(
    (proceedEmail: string) => {
      switch (stage) {
        case Stage.Email:
          setParams({ email: proceedEmail });
          setStage(Stage.Profile);
          break;
        case Stage.Profile:
          navigate(Routes.JoinCompany);
          break;
        default:
          break;
      }
    },
    [setParams, setStage, stage, navigate]
  );

  let content = null;
  switch (stage) {
    case Stage.Profile:
      content = <CreateProfile proceed={proceed} />;
      break;
    case Stage.Email:
    default:
      content = <CreateEmail proceed={proceed} />;
      break;
  }

  // While loading, or if there is an email and it's idle, show a spinner
  if (getStatus.isLoading || (email && getStatus.isIdle)) {
    content = (
      <div className="p-8 w-full h-full">
        <BlueCargoLogo variant="larger-blue" />
        <div className="w-full h-full flex items-center justify-center">
          <CircularProgress />
        </div>
      </div>
    );
  }

  return (
    <SignUpTemplate className="p-0" noLogo>
      {content}
    </SignUpTemplate>
  );
};
SignUp.displayName = 'Pages.SignUp';
